import type { ReactNode } from 'react';
import { ChevronDoubleLeftIcon } from '../../icons/ChevronDoubleLeftIcon';
import { ChevronDoubleRightIcon } from '../../icons/ChevronDoubleRightIcon';
import { ChevronLeftIcon } from '../../icons/ChevronLeftIcon';
import { ChevronRightIcon } from '../../icons/ChevronRightIcon';
import { narrowPages, pageSlots } from './Pagination.pages';
import { PageRun, PaginationNav, Slot } from './Pagination.styles';
import type { PaginationProps } from './Pagination.types';

const defaultPageLabel = (page: number, totalPages: number) => `Page ${page} of ${totalPages}`;

/**
 * Numbered pagination over a result set. It renders and reports: the caller owns the current page,
 * the total, and where each page goes.
 *
 * Two modes, and they cannot share one element. Navigated mode draws anchors, so every page has a
 * URL that can be shared, middle-clicked and crawled, and turning a page needs no JavaScript.
 * Picked mode draws buttons and reports through `onChange`.
 *
 * A chevron with nowhere to go becomes a disabled button in either mode, keeping its slot so the
 * run does not shift as you page. A disabled button is the one thing both modes can say: an anchor
 * cannot be disabled, and hiding the control instead would leave a reader unaware it was ever
 * there.
 *
 * Below 600px the run narrows to the window alone — the first and last anchors and the ellipses
 * carry `data-wide` and are hidden, leaving three numbers between the chevrons. Every page stays in
 * the markup at every width; CSS decides which are shown. See `Pagination.styles` for why.
 *
 * Losing those anchors also loses the only way to reach either end, so the narrow form adds a
 * double chevron on each side when that end is out of the window. They are the one part of the
 * control the wide form does not draw, because there the run already lists both ends.
 */
export function Pagination(props: PaginationProps) {
  const {
    page,
    totalPages,
    navigationLabel,
    previousLabel = 'Previous page',
    nextLabel = 'Next page',
    firstLabel = 'First page',
    lastLabel = 'Last page',
    pageLabel = defaultPageLabel,
  } = props;

  const slots = pageSlots({ page, totalPages });

  if (slots.length === 0) {
    return null;
  }

  const narrow = new Set(narrowPages({ page, totalPages }));

  /*
   * A jump is drawn only when its end is out of reach — which is what "far from the end" means
   * once the narrow form has hidden the first and last anchors. The wide run always lists both,
   * so CSS hides these there; see `data-jump` in `Pagination.styles`.
   */
  const canJumpToFirst = !narrow.has(1);
  const canJumpToLast = !narrow.has(totalPages);

  /** Both modes draw the same dead end, so neither has to invent one. */
  const spentChevron = (label: string, glyph: ReactNode) => (
    <Slot as="button" type="button" disabled data-step aria-label={label}>
      {glyph}
    </Slot>
  );

  const run = (renderNumber: (at: number, wide: boolean) => ReactNode) => (
    <PageRun>
      {slots.map((slot, index) =>
        slot === 'gap' ? (
          // Keyed by the page it follows: a gap has no identity of its own, but its position does.
          <Slot
            key={`gap-after-${slots[index - 1]}`}
            as="span"
            data-decorative
            data-wide
            aria-hidden="true"
          >
            &hellip;
          </Slot>
        ) : (
          renderNumber(slot, !narrow.has(slot))
        )
      )}
    </PageRun>
  );

  if (props.hrefFor) {
    const { hrefFor, linkComponent = 'a' } = props;

    const step = (target: number, label: string, glyph: ReactNode) =>
      target < 1 || target > totalPages ? (
        spentChevron(label, glyph)
      ) : (
        <Slot
          as={linkComponent}
          href={hrefFor(target)}
          rel={target < page ? 'prev' : 'next'}
          data-step
          aria-label={label}
        >
          {glyph}
        </Slot>
      );

    const jump = (target: number, label: string, glyph: ReactNode) => (
      <Slot as={linkComponent} href={hrefFor(target)} data-step data-jump aria-label={label}>
        {glyph}
      </Slot>
    );

    return (
      <PaginationNav aria-label={navigationLabel}>
        {canJumpToFirst && jump(1, firstLabel, <ChevronDoubleLeftIcon />)}
        {step(page - 1, previousLabel, <ChevronLeftIcon />)}
        {run((at, wide) => (
          <Slot
            key={at}
            as={linkComponent}
            href={hrefFor(at)}
            data-wide={wide || undefined}
            aria-label={pageLabel(at, totalPages)}
            aria-current={at === page ? 'page' : undefined}
          >
            {at}
          </Slot>
        ))}
        {step(page + 1, nextLabel, <ChevronRightIcon />)}
        {canJumpToLast && jump(totalPages, lastLabel, <ChevronDoubleRightIcon />)}
      </PaginationNav>
    );
  }

  const { onChange } = props;

  const pick = (target: number, label: string, glyph: ReactNode) =>
    target < 1 || target > totalPages ? (
      spentChevron(label, glyph)
    ) : (
      <Slot as="button" type="button" data-step aria-label={label} onClick={() => onChange(target)}>
        {glyph}
      </Slot>
    );

  const jump = (target: number, label: string, glyph: ReactNode) => (
    <Slot
      as="button"
      type="button"
      data-step
      data-jump
      aria-label={label}
      onClick={() => onChange(target)}
    >
      {glyph}
    </Slot>
  );

  return (
    <PaginationNav aria-label={navigationLabel}>
      {canJumpToFirst && jump(1, firstLabel, <ChevronDoubleLeftIcon />)}
      {pick(page - 1, previousLabel, <ChevronLeftIcon />)}
      {run((at, wide) => (
        <Slot
          key={at}
          as="button"
          type="button"
          data-wide={wide || undefined}
          aria-label={pageLabel(at, totalPages)}
          aria-current={at === page ? 'page' : undefined}
          onClick={() => onChange(at)}
        >
          {at}
        </Slot>
      ))}
      {pick(page + 1, nextLabel, <ChevronRightIcon />)}
      {canJumpToLast && jump(totalPages, lastLabel, <ChevronDoubleRightIcon />)}
    </PaginationNav>
  );
}
